import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Download } from "lucide-react";
import { API, useAuth } from "../App";
import { Button } from "../components/ui/button";
import SojournsSeoCard from "../components/admin/SojournsSeoCard";
import SojournsNotesMail from "../components/admin/SojournsNotesMail";
import SojournsGateMail from "../components/admin/SojournsGateMail";
import LinkPreviewCard from "../components/admin/LinkPreviewCard";
import FounderEditionCard from "../components/admin/FounderEditionCard";

// Admin: Savi Sojourns sign-ups (newest first), CSV export, and the two page settings.
export default function AdminSojournsPage() {
  const { token, user } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [rows, setRows] = useState([]);
  const [settings, setSettings] = useState({ reveal_date: "", edition: "001", invite_code: "", seats_held: [], private: { destination: "", coords: "", note: "" } });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [apps, setApps] = useState([]);

  useEffect(() => {
    axios.get(`${API}/admin/sojourns/signups`, { headers })
      .then((r) => {
        setRows(r.data.signups || []);
        setApps(r.data.applications || []);
        const s = r.data.settings || {};
        setSettings({ reveal_date: s.reveal_date || "", edition: s.edition || "001", invite_code: s.invite_code || "", seats_held: s.seats_held || [], private: { destination: "", coords: "", note: "", ...(s.private || {}) } });
      })
      .catch(() => toast.error("Couldn't load sign-ups"))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const downloadCsv = async (kind = "signups") => {
    try {
      const r = await axios.get(`${API}/admin/sojourns/${kind}.csv`, { headers, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `sojourns-${kind}-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Couldn't download the CSV");
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    const put = (key, value) => axios.put(`${API}/admin/site-settings/${key}`, { value: (value || "").trim() }, { headers });
    try {
      await Promise.all([
        put("sojourns_reveal_date", settings.reveal_date),
        put("sojourns_edition", settings.edition || "001"),
        put("sojourns_invite_code", settings.invite_code),
        put("sojourns_private_destination", settings.private.destination),
        put("sojourns_private_coords", settings.private.coords),
        put("sojourns_private_note", settings.private.note),
        put("sojourns_seats_held", settings.seats_held.join(",")),
      ]);
      toast.success("Sojourns settings saved");
    } catch {
      toast.error("Couldn't save settings");
    } finally {
      setSaving(false);
    }
  };

  const setPrivate = (k) => (e) => setSettings({ ...settings, private: { ...settings.private, [k]: e.target.value } });
  const toggleSeat = (n) => setSettings({ ...settings, seats_held: settings.seats_held.includes(n) ? settings.seats_held.filter((s) => s !== n) : [...settings.seats_held, n].sort((a, b) => a - b) });
  const inputCls = "h-10 w-full px-3 rounded-lg border border-stone-200 text-sm bg-white";

  const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" }) : "–");

  return (
    <div className="min-h-screen bg-[#FBF7F1] p-4 sm:p-8" data-testid="admin-sojourns-page">
      <div className="max-w-5xl mx-auto">
        <Link to="/admin" className="inline-flex items-center gap-1.5 text-sm text-[#3D4035]/70 hover:text-[#C85A38] mb-6" data-testid="admin-sojourns-back">
          <ArrowLeft className="w-4 h-4" /> Admin
        </Link>
        <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
          <div>
            <h1 className="font-serif text-3xl text-[#2E2A26]">Sojourns sign-ups</h1>
            <p className="text-sm text-[#3D4035]/60 mt-1" data-testid="admin-sojourns-count">{rows.length} on the list · newest first</p>
          </div>
          <Button onClick={() => downloadCsv("signups")} disabled={rows.length === 0} className="bg-[#C85A38] hover:bg-[#A84830] text-white rounded-full" data-testid="admin-sojourns-csv">
            <Download className="w-4 h-4 mr-2" /> Download CSV
          </Button>
        </div>

        <div className="bg-white rounded-2xl border border-stone-200 p-5 mb-6" data-testid="admin-sojourns-settings">
          <h2 className="font-medium text-[#2E2A26] mb-3">Page settings</h2>
          <div className="grid sm:grid-cols-[1fr_1fr_auto] gap-4 items-end">
            <div>
              <label htmlFor="sj-reveal" className="block text-xs text-[#3D4035]/60 mb-1">Gate opens (countdown counts to this; from that day the page takes applications)</label>
              <input id="sj-reveal" type="date" value={settings.reveal_date} onChange={(e) => setSettings({ ...settings, reveal_date: e.target.value })} className="h-10 w-full px-3 rounded-lg border border-stone-200 text-sm bg-white" data-testid="admin-sojourns-reveal-date" />
            </div>
            <div>
              <label htmlFor="sj-edition" className="block text-xs text-[#3D4035]/60 mb-1">Edition label (shown as 'No. 001')</label>
              <input id="sj-edition" type="text" value={settings.edition} onChange={(e) => setSettings({ ...settings, edition: e.target.value })} maxLength={8} className="h-10 w-full px-3 rounded-lg border border-stone-200 text-sm bg-white" data-testid="admin-sojourns-edition" />
            </div>
            <Button onClick={saveSettings} disabled={saving} variant="outline" className="rounded-full h-10" data-testid="admin-sojourns-save-settings">
              {saving ? "Saving" : "Save"}
            </Button>
          </div>
          <h3 className="font-medium text-[#2E2A26] mt-6 mb-1">Seats held</h3>
          <p className="text-xs text-[#3D4035]/60 mb-3">Tap a seat to mark it held. The boarding pass shows twelve seats, held ones filled. <span data-testid="admin-sojourns-seats-count">{settings.seats_held.length} of 12 held.</span></p>
          <div className="grid grid-cols-6 gap-2 max-w-xs mb-2" data-testid="admin-sojourns-seats">
            {Array.from({ length: 12 }, (_, i) => i + 1).map((n) => {
              const held = settings.seats_held.includes(n);
              return (
                <button key={n} type="button" onClick={() => toggleSeat(n)} aria-pressed={held} className={`h-10 text-sm border transition-colors ${held ? "bg-[#2E2A26] text-white border-[#2E2A26]" : "bg-white text-[#2E2A26] border-stone-300 hover:border-[#2E2A26]"}`} data-testid={`admin-sojourns-seat-${n}`}>
                  {n}
                </button>
              );
            })}
          </div>
          <h3 className="font-medium text-[#2E2A26] mt-6 mb-1">Invitation code</h3>
          <p className="text-xs text-[#3D4035]/60 mb-3">With a code set, the page shows "Have an invitation code?". A correct code lifts the black bars: the destination appears on the ticket and the chart, with the position and your note. Leave the code empty to hide the option.</p>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="sj-code" className="block text-xs text-[#3D4035]/60 mb-1">Code (not case sensitive)</label>
              <input id="sj-code" type="text" value={settings.invite_code} onChange={(e) => setSettings({ ...settings, invite_code: e.target.value })} maxLength={80} className={inputCls} data-testid="admin-sojourns-invite-code" />
            </div>
            <div>
              <label htmlFor="sj-dest" className="block text-xs text-[#3D4035]/60 mb-1">Destination, as it should read when opened</label>
              <input id="sj-dest" type="text" value={settings.private.destination} onChange={setPrivate("destination")} maxLength={120} placeholder="Mallorca" className={inputCls} data-testid="admin-sojourns-private-destination" />
            </div>
            <div>
              <label htmlFor="sj-coords" className="block text-xs text-[#3D4035]/60 mb-1">Position (shown on the chart)</label>
              <input id="sj-coords" type="text" value={settings.private.coords} onChange={setPrivate("coords")} maxLength={80} placeholder="39°37′N 2°59′E" className={inputCls} data-testid="admin-sojourns-private-coords" />
            </div>
            <div>
              <label htmlFor="sj-note" className="block text-xs text-[#3D4035]/60 mb-1">Private note (replaces "Revealed to accepted applicants.")</label>
              <input id="sj-note" type="text" value={settings.private.note} onChange={setPrivate("note")} maxLength={200} placeholder="Dates and the fare follow by email." className={inputCls} data-testid="admin-sojourns-private-note" />
            </div>
          </div>
        </div>

        <FounderEditionCard headers={headers} />
        <SojournsSeoCard headers={headers} />
        <LinkPreviewCard headers={headers} />
        <SojournsNotesMail headers={headers} adminEmail={user?.email} />
        <SojournsGateMail headers={headers} adminEmail={user?.email} />

        <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden">
          {loading ? (
            <p className="p-6 text-sm text-[#3D4035]/60">Loading</p>
          ) : rows.length === 0 ? (
            <p className="p-6 text-sm text-[#3D4035]/60" data-testid="admin-sojourns-empty">Nobody has registered yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="admin-sojourns-table">
                <thead className="bg-[#F4ECE2] text-left text-xs uppercase tracking-wider text-[#3D4035]/60">
                  <tr>
                    <th className="px-4 py-3">When</th>
                    <th className="px-4 py-3">Name</th>
                    <th className="px-4 py-3">Email</th>
                    <th className="px-4 py-3">Business</th>
                    <th className="px-4 py-3">Link</th>
                    <th className="px-4 py-3">Source</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                  {rows.map((r) => (
                    <tr key={r.id} data-testid={`admin-sojourns-row-${r.id}`}>
                      <td className="px-4 py-3 whitespace-nowrap text-[#3D4035]/70">{fmt(r.created_at)}</td>
                      <td className="px-4 py-3 font-medium text-[#2E2A26]">{r.name}</td>
                      <td className="px-4 py-3"><a href={`mailto:${r.email}`} className="text-[#C85A38] hover:underline">{r.email}</a></td>
                      <td className="px-4 py-3 max-w-[260px] truncate" title={r.business}>{r.business || "–"}</td>
                      <td className="px-4 py-3 max-w-[200px] truncate">{r.link ? <a href={/^https?:/i.test(r.link) ? r.link : `https://${r.link}`} target="_blank" rel="noopener noreferrer" className="text-[#C85A38] hover:underline">{r.link}</a> : "–"}</td>
                      <td className="px-4 py-3 text-[#3D4035]/60 whitespace-nowrap">{[r.utm_source, r.utm_medium, r.utm_campaign].filter(Boolean).join(" / ") || "–"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-end justify-between gap-4 mt-10 mb-4">
          <div>
            <h2 className="font-serif text-2xl text-[#2E2A26]">Applications</h2>
            <p className="text-sm text-[#3D4035]/60 mt-1" data-testid="admin-sojourns-apps-count">{apps.length} received · newest first</p>
          </div>
          <Button onClick={() => downloadCsv("applications")} disabled={apps.length === 0} variant="outline" className="rounded-full" data-testid="admin-sojourns-apps-csv">
            <Download className="w-4 h-4 mr-2" /> Download CSV
          </Button>
        </div>
        <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden">
          {apps.length === 0 ? (
            <p className="p-6 text-sm text-[#3D4035]/60" data-testid="admin-sojourns-apps-empty">No applications yet. The form switches to applications on the gate date.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="admin-sojourns-apps-table">
                <thead className="bg-[#F4ECE2] text-left text-xs uppercase tracking-wider text-[#3D4035]/60">
                  <tr>
                    <th className="px-4 py-3">When</th>
                    <th className="px-4 py-3">Name</th>
                    <th className="px-4 py-3">Email</th>
                    <th className="px-4 py-3">Business</th>
                    <th className="px-4 py-3">Goal</th>
                    <th className="px-4 py-3">Link</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                  {apps.map((r) => (
                    <tr key={r.id} data-testid={`admin-sojourns-app-${r.id}`}>
                      <td className="px-4 py-3 whitespace-nowrap text-[#3D4035]/70">{fmt(r.created_at)}</td>
                      <td className="px-4 py-3 font-medium text-[#2E2A26]">{r.name}</td>
                      <td className="px-4 py-3"><a href={`mailto:${r.email}`} className="text-[#C85A38] hover:underline">{r.email}</a></td>
                      <td className="px-4 py-3 max-w-[220px] truncate" title={r.business}>{r.business || "–"}</td>
                      <td className="px-4 py-3 max-w-[260px] truncate" title={r.goal}>{r.goal || "–"}</td>
                      <td className="px-4 py-3 max-w-[180px] truncate">{r.link ? <a href={/^https?:/i.test(r.link) ? r.link : `https://${r.link}`} target="_blank" rel="noopener noreferrer" className="text-[#C85A38] hover:underline">{r.link}</a> : "–"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
